import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import InputDate from "@/atoms-react/input/InputDate.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import type { DeclarativeBirth } from "@/features/flow/types";
import type { AddressCreate, NaturalPerson } from "@/features/user/types";
import { FlowHeader } from "@/flow/components/flow-header.react";
import { FormAddAddress } from "@/molecules/form-add-address/index.react";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";
import { parseDateToUTC } from "@/utils/date.utils";
import { isBirthDateValid, isDeclarativeBirthValid } from "@/utils/declarative.utils";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import _ from "lodash";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface DeclarativeIdentityBirthProps extends LayoutBaseProps {
  onValidated?: (output: DeclarativeIdentityBirthOutput) => void;
  declarativeBirth?: DeclarativeBirth;
  profile?: NaturalPerson | null;
  personNationality?: string[];
}

export type DeclarativeIdentityBirthOutput = DeclarativeBirth;

function DeclarativeIdentityBirth({
  onValidated = () => {},
  declarativeBirth,
  personNationality,
  profile
}: DeclarativeIdentityBirthProps) {
  const { t } = useTranslation(["pass", "atoms"]);
  const dispatch = useAppDispatch();
  const handleTimeZone = (declarativeBirth: Date) => {
    const localTimeZoneOffset = new Date().getTimezoneOffset();
    declarativeBirth.setMinutes(declarativeBirth.getMinutes() - localTimeZoneOffset);
    return declarativeBirth;
  };
  const countryCode = declarativeBirth?.countryCode
    ? declarativeBirth?.countryCode
    : (profile?.countryOfBirth ?? "");
  const initialData: DeclarativeBirth = {
    birth: declarativeBirth?.birth
      ? handleTimeZone(declarativeBirth?.birth)
      : profile?.DateOfBirth?.date
        ? parseDateToUTC(profile.DateOfBirth.date)
        : null,
    countryCode: countryCode.toString(),
    cityOfBirth: declarativeBirth?.cityOfBirth
      ? declarativeBirth?.cityOfBirth
      : (profile?.StateOfBirth ?? ""),
    zip: declarativeBirth?.zip ?? null
  };
  const [data, setData] = useState<DeclarativeBirth>(initialData);
  const [addressValid, setAddressValid] = useState(isDeclarativeBirthValid(data));

  function execute() {
    if (!isDeclarativeBirthValid(data) || !addressValid) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    if (!isBirthDateValid(data.birth)) {
      toast.error(t("flow.id_control.error.must_be_18_plus"));
      return;
    }

    const countryFrOrDomTomId = [
      "10",
      "110",
      "151",
      "100",
      "197",
      "155",
      "191",
      "195",
      "244",
      "102",
      "167",
      "103"
    ];
    if (countryFrOrDomTomId.includes(data?.countryCode as string) && !data.zip) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    dispatch(setIsInPreviousStep(false));
    onValidated(data);
  }

  return (
    <>
      <Column className="justify-between">
        <div className="mb-12">
          <FlowHeader
            title={t("flow.id_control.signup.declarative_identity.label.two_title")}
            description={t("flow.id_control.signup.declarative_identity.label.two_description")}
            onPreviousClick={() => dispatch(setIsInPreviousStep(true))}
          />
          <div className="flex w-full flex-row">
            {" "}
            <InputDate
              autoComplete="bday"
              value={data?.birth ?? null}
              label={t("flow.id_control.input.birth_date_label")}
              placeholder={t("flow.id_control.input.birth_date_placeholder")}
              className="w-full"
              onChange={(date) => {
                if (date && !Array.isArray(date)) {
                  setData((data) => ({ ...data, birth: date }));
                } else if (date === null) {
                  setData((data) => ({ ..._.omit(data, "birth") }));
                }
              }}
            />
          </div>
          <FormAddAddress
            personNationality={personNationality}
            autoFocus
            fields={{
              tag: { visible: false },
              address: { visible: false },
              city: {
                visible: true,
                label: t("flow.id_control.input.city_of_birth_label")
              },
              zip: {
                visible: false
              },
              country: {
                visible: true,
                label: t("flow.id_control.input.country_of_birth_label"),
                placeholder: t("flow.id_control.input.country_of_birth_placeholder")
              }
            }}
            address={{
              address: "",
              zip: data.zip ?? "",
              country: parseInt(data.countryCode ?? "-1"),
              city: data.cityOfBirth ?? "",
              tag: "Résidence principale"
            }}
            onChange={(address: AddressCreate, valid) => {
              setAddressValid(valid);
              setData((data) => ({
                ...data,
                cityOfBirth: `${address.city}`,
                zip: address.zip,
                countryCode: (address.country ?? "").toString()
              }));
            }}
            className={"mt-0 mb-0"}
          />
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <DotsNavigation total={8} current={1} />
          <Button
            shortcut="enter"
            onClick={() => execute()}
            icon={<ChevronRightIcon />}
            className="mt-4"
          >
            {t("flow.id_control.input.continue")}
          </Button>
        </div>
      </Column>
    </>
  );
}

export default DeclarativeIdentityBirth;
